//@ts-nocheck
import { transformFrontendConditionsToApiFormat } from './filter-transformer';

import { chain } from 'lodash';

import type { Edge, Node } from 'reactflow';

class TqlService {
  private nodes: Node[];
  private edges: Edge[];
  private links: boolean;

  constructor({ nodes, edges, links }) {
    this.nodes = nodes ?? [];
    this.edges = edges ?? [];
    this.links = links ?? true;
  }

  getCits({ nodes }: { nodes: Node[] }) {
    return nodes.reduce((acc, curr) => {
      if (curr?.id) {
        const cit = {
          condition: 'and',
          matchers: [
            {
              operator: FILTER_OPERATOR_TYPE.EQUALS,
              target: FILTER_TARGET_TYPE.CIT_NAME,
              value: curr?.data.name,
              displayName: curr.data.label,
            },
          ],
          visible: curr.data.visible ?? true,
        };

        // Вложенные фильтры CIT
        if (curr?.data?.conditions?.wrapper?.[0]?.matchers?.length) {
          const conditions = transformFrontendConditionsToApiFormat(
            curr?.data?.conditions,
          );
          cit.matchers = cit.matchers.concat(conditions);
        }

        acc[curr?.data.filterId] = cit;
      }

      return acc;
    }, {});
  }

  getFilterMatcherCits({ cits }) {
    const citKeys = Object.keys(cits ?? {});

    if (citKeys.length === 1) {
      return {
        condition: FILTER_CONDITION_TYPE.ONE,
        matchers: citKeys,
      };
    } else if (citKeys.length > 1) {
      return {
        condition: FILTER_CONDITION_TYPE.OR,
        matchers: citKeys,
      };
    }

    return null;
  }

  getFilterMatcher({
    cits,
    edges,
    nodes,
  }: {
    edges: Edge[];
    nodes: Node[];
    cits: any;
  }) {
    // Граф со связями.
    if (edges?.length) {
      return this.edgesToMatchers(edges, nodes);
    }
    // Граф без связей.
    else {
      return this.getFilterMatcherCits({ cits });
    }
  }

  edgesToMatchers(edges: Edge[], nodes: Node[]) {
    const matchers = edges.reduce((acc, curr) => {
      const limitations = curr?.data?.limitations;
      const source = nodes.find((x) => x.id === curr.source);
      const target = nodes.find((x) => x.id === curr.target);
      acc.push({
        value: curr.data.name,
        operator: FILTER_OPERATOR_TYPE.EQUALS,
        target: FILTER_TARGET_TYPE.LINK_NAME,

        from: source.data.filterId.toString(),
        to: target.data.filterId.toString(),
        ...(limitations
          ? {
              quantifier_side: limitations.target,
              quantifier: { from: limitations.from, to: limitations.to },
            }
          : {}),
      });
      return acc;
    }, []);
    return {
      condition: FILTER_CONDITION_TYPE.JOIN_QUANTIFIER,
      matchers,
    };
  }

  getFilter({ nodes, edges }: { nodes: Node[]; edges: Edge[] }) {
    const groups = chain(nodes)
      .groupBy('data.group')
      .map((value, key) => ({ group: key, nodes: value }))
      .value();

    const matchers = groups.reduce((acc, curr) => {
      const nodeIds = curr.nodes.map((x) => x.id);
      const matcher = this.getFilterMatcher({
        cits: this.getCits({ nodes: curr.nodes }),
        edges: edges.filter(
          (x) => nodeIds.includes(x.source) || nodeIds.includes(x.target),
        ),
        nodes: nodes,
      });
      acc.push(matcher);
      return acc;
    }, []);

    if (matchers.length > 1) {
      return {
        condition: FILTER_CONDITION_TYPE.OR,
        matchers: matchers,
      };
    } else {
      return matchers[0];
    }
  }

  getIsReturnLinks() {
    return this.links;
  }

  getQuery() {
    const result: any = {};

    result.cits = this.getCits({ nodes: this.nodes });
    result.filter =
      this.getFilter({ nodes: this.nodes, edges: this.edges }) ?? {};
    result.links = this.getIsReturnLinks();

    return result;
  }
}

export const FILTER_TARGET_TYPE = {
  CIT_UUID: 'CIT_UUID',
  CIT_NAME: 'CIT_NAME',
  LINK_UUID: 'LINK_UUID',
  LINK_NAME: 'LINK_NAME',
  CI_STATUS: 'CI_STATUS',
  ATTRIBUTE_VALUE: 'ATTRIBUTE_VALUE',
};

export const FILTER_CONDITION_TYPE = {
  ONE: 'one',
  OR: 'or',
  JOIN_QUANTIFIER: 'join_quantifier',
  JOIN_DISTINCT: 'join_distinct',
};

export const FILTER_OPERATOR_TYPE = {
  EQUALS: 'equals',
};

export const transformFlowToApiFormat = ({
  nodes,
  edges,
  links,
}: {
  nodes: Node[];
  edges: Edge[];
  links?: boolean;
}) => {
  try {
    const tqlService = new TqlService({
      nodes,
      edges,
      links,
    });

    return tqlService.getQuery();
  } catch (e) {
    console.error(e);
    return null;
  }
};